import { DOCUMENT, NgIf, NgIfContext } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    Renderer2,
    TemplateRef,
    inject,
} from '@angular/core';

import { AutomationModule } from '@frontend/sports/automation/core-feature';
import { PopupsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { OptionalNonRootToken } from '@frontend/sports/host-app/sports-product/feature/non-root-token';
import { MODAL_WINDOW_CONFIG } from '@frontend/sports/modal/feature/provider';
import { Store } from '@ngrx/store';

import { ModalAnimationHelperService } from '../dialog/modal-animation-helper.service';
import { ModalGestureService, ModalGestureVerticalConfig, VERTICAL_GESTURE_CONFIG } from '../dialog/modal-gesture.service';
import { HORIZONTAL_GESTURE_CONFIG, ModalGestureHorizontalConfig, ModalHorizontalGestureService } from '../dialog/modal-horizontal-gesture.service';
import { popupManualClose } from './actions';
import { ModalOptions } from './modal';
import { ModalDismissReasons } from './modal-dismiss-reasons';

export enum PopupAction {
    Open = 'open',
    ManualClose = 'manualClose',
    CloseByNavigation = 'closeByNavigation',
    CloseByMybets = 'CloseByMybets',
    CloseByNextPopup = 'CloseByNextPopup',
    CloseByPan = 'CloseByPan',
}

export interface PopupClosingResult {
    closeAction: PopupAction;
}

export enum CloseButtonType {
    Icon = 'Icon',
    Button = 'Button',
}
export interface GestureOptions {
    enabled?: boolean;
    headerSelector?: string;
    scrollTargetQuerySelector?: string;
}

@Component({
    selector: 'ms-modal-window',
    templateUrl: 'modal-window.html',
    standalone: true,
    imports: [NgIf, AutomationModule],
    providers: [
        ModalGestureService,
        ModalHorizontalGestureService,
        {
            provide: VERTICAL_GESTURE_CONFIG,
            useClass: ModalGestureVerticalConfig,
        },
        {
            provide: HORIZONTAL_GESTURE_CONFIG,
            useClass: ModalGestureHorizontalConfig,
        },
    ],
})
export class ModalWindowComponent implements OnInit, AfterViewInit, OnDestroy {
    @HostBinding('attr.aria-labelledby')
    @Input()
    ariaLabelledBy: string;

    /**
     * Provide a template to be rendered inside the modal-header.
     *
     * @type {TemplateRef<any>}
     * @memberof ModalOptions
     */
    @Input()
    headerTemplate: TemplateRef<NgIfContext>;

    slideOutFromTopToButtom = 'modal-slide-out-to-bottom';
    slideOutToRight = 'modal-slide-out-to-right';

    @Input() backdrop: boolean | string = true;
    @Input() centered: string;
    @Input() keyboard = true;
    @Input() size: string;
    @Input() windowClass: string;
    @Input() showDialogHeader?: boolean;
    @Input() title?: string;
    @Input() hideCloseButton?: boolean;
    @Input() showBackNav?: boolean;
    @Input() animatePopupDismiss = false;
    @Input() showCloseIcon = true;
    @Input() closePrevious?: boolean;
    @Input() gestureOptions: GestureOptions;

    @Output() dismiss = new EventEmitter<{ closeAction: PopupAction.ManualClose }>();

    @HostBinding('attr.role') role = 'dialog';
    @HostBinding('attr.tabindex') tabindex = '-1';

    @HostBinding()
    get class(): string {
        return 'modal ' + (this.windowClass ? ' ' + this.windowClass : '');
    }

    showCloseBtnText?: boolean;

    private readonly _doc = inject(DOCUMENT);

    constructor(
        public sitecore: Sitecore,
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private timerService: TimerService,
        private store: Store,
        private modalGestureService: ModalGestureService,
        private modalAnimationHelper: ModalAnimationHelperService,
        private popUpsConfig: PopupsConfig,
        private modalHorizontalGestureService: ModalHorizontalGestureService,
        @Optional() @Inject(OptionalNonRootToken(MODAL_WINDOW_CONFIG)) private config: Partial<ModalOptions> | undefined,
    ) {
        if (this.config) {
            this.animatePopupDismiss = this.config.animatePopupDismiss ?? false;
            this.showCloseIcon = this.popUpsConfig.closeButtonType === CloseButtonType.Icon || (this.config.showCloseIcon ?? true);
        }
    }

    close(): void {
        if (this.gestureOptions.enabled) {
            this.modalAnimationHelper.addSlideWithScale();
        } else {
            this.renderer.addClass(this.elementRef.nativeElement, this.slideOutFromTopToButtom);
        }

        this.dismissPopup();

        this.store.dispatch(popupManualClose());
    }

    goBack(): void {
        this.renderer.addClass(this.elementRef.nativeElement, this.slideOutToRight);
        this.dismissPopup();
    }

    private dismissPopup() {
        if (this.animatePopupDismiss) {
            this.timerService.setTimeoutOutsideAngular(() => {
                this.dismiss.emit({ closeAction: PopupAction.ManualClose });
            }, 500);
        } else {
            this.dismiss.emit({ closeAction: PopupAction.ManualClose });
        }
    }

    @HostListener('mousedown', ['$event'])
    @HostListener('touch', ['$event'])
    backdropClick(event: Event): void {
        if (this.backdrop === true && this.elementRef.nativeElement === event.target) {
            this.dismissWindow(ModalDismissReasons.BackdropClick);
        }
    }

    //Prevent any scroll event within modal container to bubble up to body (preventing unwanted double scroll appearing)
    @HostListener('scroll', ['$event'])
    @HostListener('touchmove', ['$event'])
    preventScrolPropogation(event: Event) {
        event.stopPropagation();
    }

    @HostListener('keyup.esc', ['$event'])
    escKey(event: Event): void {
        if (this.keyboard && !event.defaultPrevented) {
            this.dismissWindow(ModalDismissReasons.Esc);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private dismissWindow(reason: any): void {
        if (this.animatePopupDismiss) {
            this.renderer.addClass(this.elementRef.nativeElement, this.slideOutFromTopToButtom);
            this.timerService.setTimeoutOutsideAngular(() => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                this.dismiss.emit(reason);
            }, 500);
        } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.dismiss.emit(reason);
        }
    }

    ngOnInit(): void {
        this.renderer.addClass(this._doc.body, 'modal-open');
    }

    ngAfterViewInit(): void {
        this.timerService.setTimeoutOutsideAngular(() => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            if (!this.elementRef.nativeElement.contains(this._doc.activeElement)) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                this.elementRef.nativeElement['focus'].apply(this.elementRef.nativeElement, []);
            }
        });

        if (this.gestureOptions.headerSelector === 'event-details-modal') {
            this.modalHorizontalGestureService.addGestures(this.elementRef.nativeElement, this.gestureOptions);
        } else {
            this.modalGestureService.addGestures(this.elementRef.nativeElement, this.gestureOptions);
        }
    }

    ngOnDestroy(): void {
        this.renderer.removeClass(this._doc.body, 'modal-open');
    }
}
